import React from 'react';
import PropTypes from 'prop-types';
import styles from './cscb.scss';
import navigationService from '../../../../services/NavigationService';
import { t } from '../../../../services/i18nService';
import customFieldsService from '../../../../services/CustomFieldsService';
import customFieldsConstants from '../../../../constants/CustomFieldsConstants';
import assessmentReader from '../../../../store/readHelpers/AssessmentReader';
import questionSelectionReader from '../../../../store/readHelpers/QuestionSelectionReader';
import questionSelectionService from '../../../../services/QuestionSelectionService';
import { reportError } from './../../../../services/ErrorReporting';

function CscbQuestionInfo({
    currentQuestion,
    section,
    currentInlineSection,
    inlineSectionNumber,
}) {
    const handleBackBtnClick = () => {
        if (currentQuestion?.inlineSectionId) {
            navigationService.navigateToPath(`inline/${currentQuestion?.inlineSectionId}/cscb`);
        } else {
            reportError('Cannot navigate: currentQuestion.inlineSectionId is not provided in CscbQuestionInfo');
        }
    };

    const isMetaDataMatchingForCSCB = (focusLevel, customFields) => {
        const productFieldValue = customFieldsService.getCustomFieldValue(customFields, customFieldsConstants.PRODUCT);
        const isWriting = focusLevel.includes(customFieldsConstants.TEST_TYPE_WRITING);
        return productFieldValue === customFieldsConstants.PRODUCT_GT_IOC && isWriting;
    };

    const currentSection = section?.sections?.find(sectionData => sectionData?.id === currentQuestion?.parentId);
    let isCSCBRubric = false;
    const sectionNumber = currentSection ? section?.sections?.findIndex(sectionData => sectionData?.id === currentSection.id) : -1;
    if (currentSection?.customFields) {
        const focusLevel = customFieldsService.getCustomFieldValue(currentSection?.customFields, customFieldsConstants.TESTING_FOCUS_LEVEL_1_FJORD);
        isCSCBRubric = focusLevel ? isMetaDataMatchingForCSCB(focusLevel, currentSection?.customFields) : false;
    }

    const sections = assessmentReader.getSections();
    const partNumber = currentInlineSection
        ? sections.findIndex(sectionData => sectionData?.id === currentInlineSection?.sectionId)
        : -1;

    const getSelectedQuestionInfo = () => {
        const selectedQuestion = currentInlineSection?.
            questionPairs?.
            find(question => question.isSelected === questionSelectionReader.selectionType.YES);
        if (!selectedQuestion) return { hasSelectedQuestion: false, questionNumber: 0 };

        const selectedSection = currentInlineSection?.sections?.find(sectionData => sectionData?.id === selectedQuestion?.parentId);
        if (!selectedSection) return { hasSelectedQuestion: false, questionNumber: 0 };

        const selectedPartNumber = currentInlineSection?.sections ?
            currentInlineSection.sections.findIndex(sectionData => sectionData?.id === selectedSection.id) : -1;

        return { hasSelectedQuestion: true, questionNumber: selectedPartNumber };
    };

    const { hasSelectedQuestion, questionNumber: selectedQuestionPartNumber } = getSelectedQuestionInfo();
    const showBackButton = questionSelectionService.isCandidateSelectedContentBlockEnabledForSection(section?.id);
    const isInformationSection = section?.sections?.length === 0;

    const renderBackButtonSection = () => {
        if (!showBackButton) return null;

        return (
            <React.Fragment>
                <button className={styles.cscbBackButton} onClick={handleBackBtnClick}>
                    <i className={`fa fa-arrow-left ${styles.iconSpacing}`} aria-hidden="true" />
                    {t('interactions.cscb.goBackToOptions')}
                </button>
                {isCSCBRubric ? (
                    <h1>{t(`interactions.cscb.instructionsHeading${inlineSectionNumber || 0}`,
                        { option: (sectionNumber >= 0 ? sectionNumber + 1 : 1) })}</h1>
                ) : (
                    <div>
                        {t('rubric.instructions.writing')}
                    </div>
                )}
            </React.Fragment>
        );
    };

    const renderInstructions = () => {
        if (showBackButton) return null;

        if (hasSelectedQuestion) {
            return (
                <div className={styles.mainHeading}>
                    <h1>
                        {t(`interactions.cscb.instructionsMainHeadingPart${partNumber >= 0 ? partNumber + 1 : 1}`)}
                    </h1>
                    <span>
                        {t('interactions.cscb.instructionsSubHeadingPartSelected',
                            { selectedQuestionNumber: (selectedQuestionPartNumber >= 0 ? selectedQuestionPartNumber + 1 : 1) })}
                    </span>
                </div>
            );
        }

        return (
            <div className={styles.mainHeading}>
                <h1>{t(`interactions.cscb.instructionsMainHeading${partNumber >= 0 ? partNumber + 1 : 1}`)}</h1>
                <span>{t('interactions.cscb.instructionsDetails')}</span>
            </div>
        );
    };
    return !isInformationSection && (
        <div
            className={`${styles.cscbQuestionInfo}`}
        >
            {renderBackButtonSection()}
            {renderInstructions()}
        </div>
    );
}
CscbQuestionInfo.propTypes = {
    section: PropTypes.object,
    currentQuestion: PropTypes.object,
    currentInlineSection: PropTypes.object,
    inlineSectionNumber: PropTypes.number,
};

export default CscbQuestionInfo;
